<template>
    <article>
        <p>
            A month ago we released <a href="https://github.com/coroot/coroot" target="_blank">Coroot Community Edition</a>
            - an open-source (Apache 2.0) monitoring and troubleshooting tool for microservice architectures.
            Since then, it has been installed 400+ times and gained over 1500⭐ on GitHub.
        </p>

        <p>
            In <router-link :to="{name: 'blog', params: {name: 'pg-agent'}}">one of my previous posts</router-link>, I explained
            why we implemented another metric exporter for Postgres.
            However, collecting the right metrics is not enough to troubleshoot efficiently.
            In this post, I'll highlight the Postgres monitoring capabilities provided by Coroot.
        </p>


        <h2>Service maps</h2>

        <p>
            A database is a critical part of any infrastructure. If it fails, none of the applications depending on it will perform.
            These days, we usually have to operate systems with hundreds of services and databases,
            therefore it's really important to know which apps are using the database and how cluster components are communicating with each other.
        </p>

        <p>
            Coroot uses network-level tracing to get a comprehensive map of all network connections in a cluster.
            This approach makes it possible to track not only inbound client connections but also every connection within a database cluster such as
            Postgres-to-Postgres and Pgbouncer-to-Postgres connections.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/service-map.png" />
        </v-card>

        <p>
            Such a map not only provides superb visibility into the application topology, but it also makes it easy to identify network connectivity issues.
            For example, on the map above you can see that the primary Postgres instance <var>db-main-2</var> has no connectivity with other components.
        </p>

        <h2>Postgres SLIs (Service Level Indicators)</h2>

        <p>
            A database server like other services has its own SLIs. There are two primary SLIs of a DB instance:
        </p>

        <ul>
            <li>
                Availability: a DB instance is up and accepting client connections
            </li>
            <li>
                Latency: query execution time
            </li>
        </ul>

        <p>
            Coroot has built-in inspections to track these SLIs:
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/slis.png" />
        </v-card>

        <p>
            The worst status of these inspections is used as the status of the database,
            so you can quickly recognize if the database is experiencing any issues or not.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/overview.png" />
        </v-card>

        <h2>Automated log analysis</h2>

        <p>
            As you know, not all issues can be detected only by using metrics.
            Application logs can tell us much more, and Postgres is not an exception here.
            However, finding an issue in a large log can be pretty challenging.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/logs.png" />
        </v-card>

        <p>
            Coroot's agents extract repeated patterns from logs and turn them into metrics.
            This allows you to quickly figure out what types of Postgres errors occurred over a given period of time.
        </p>


        <h2>All relevant metrics in one place</h2>

        <p>
            Coroot displays the metrics you might need to troubleshoot any given application all in one place.
            Moreover, each subsystem has a status, so you can quickly understand if there are any issues even before opening the corresponding dashboard.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/storage.png" />
        </v-card>

        <p>To achieve this, Coroot builds a model of each infrastructure by</p>
        <ul>
            <li>grouping individual containers into applications</li>
            <li>enriching each container representation with resource consumption statistics, subsystems statuses, logged errors, and runtime-specific data</li>
            <li>linking applications to each other regarding their actual connections</li>
        </ul>

        <p>
            Using such a model allows each inspection to be aware of the instances, nodes, and upstream services related to a particular app.
        </p>


        <h2>Postgres query performance statistics</h2>

        <p>
            In the case where a Postgres server consumes more resources than usual, engineers need to figure out which queries
            caused the high CPU or I/O load. Coroot uses the query performance metrics to highlight the most CPU- and I/O-intensive queries.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/most-intensive-queries.png" />
        </v-card>

        <p>
            <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">Pg-agent</a> combines statistics
            from <var>pg_stat_activity</var> and <var>pg_stat_statements</var>, so the resulting
            metrics take into account both completed queries and those still in progress.
        </p>

        <h2>Monitoring HA Postgres clusters</h2>

        <p>
            Maintaining distributed databases in production is hard.
            Coroot provides visibility into any HA Postgres cluster with all its components and their layout across nodes and availability zones.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/cluster-network.png" />
        </v-card>

        <p>
            Moreover, it can track the current role of every Postgres instance, replication lag, and switchovers in the cluster.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/advanced-pg-monitoring/cluster.png" />
        </v-card>

        <p>
            Various Postgres deployment methods are supported out of the box:
        </p>

        <ul>
            <li>
                Dedicated Postgres instances deployed on bare metal or cloud instances
            </li>
            <li>
                Postgres in Docker
            </li>
            <li>
                Kubernetes: Postgres Operator by <a href="https://github.com/zalando/postgres-operator" target="_blank">Zalando</a>
            </li>
            <li>
                Kubernetes: Postgres Operator from <a href="https://github.com/CrunchyData/postgres-operator" target="_blank">Crunchy Data</a>
            </li>
            <li>
                AWS RDS for Postgres (including Aurora)
            </li>
        </ul>


        <h2>Conclusion</h2>

        <p>
            We believe that Coroot can make troubleshooting Postgres-related issues easier not only for experienced DBAs but
            also for engineers not specialized in databases.
        </p>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>

    </article>
</template>

<script>

export default {
    components: {}
}
</script>

<style scoped>
</style>
